/**
 * Lightweight fetch wrapper for the Sajha Social backend.
 *
 * The Go/Fiber backend wraps every response in an `ApiResponse` envelope:
 *
 *     { success, data, error, message, meta }
 *
 * This module:
 *   - stores / reads the JWT from localStorage under `sajha_token`
 *   - attaches `Authorization: Bearer <token>` to every request when present
 *   - reads `API_URL` env var (default: http://localhost:8080/api/v1)
 *   - unwraps the envelope, returning `{ data, meta }` on success and throwing
 *     an `ApiError` on failure (HTTP error OR `success: false`).
 */

export interface ApiMeta {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: ApiMeta;
}

export interface ApiResult<T> {
  data: T;
  meta?: ApiMeta;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const TOKEN_KEY = "sajha_token";
const USER_KEY = "sajha_user";

export const tokenStorage = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* ignore (e.g., private browsing) */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const userStorage = {
  get<T = unknown>(): T | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },
  set(user: unknown) {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
      /* ignore */
    }
  },
};

const DEFAULT_BASE = "http://localhost:8080/api/v1";

function baseUrl(): string {
  // Parcel inlines process.env variables at build time.
  const fromEnv = process.env.API_URL as string | undefined;
  return (fromEnv && fromEnv.trim()) || DEFAULT_BASE;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** When true, do not attach the Authorization header even if a token is set. */
  anonymous?: boolean;
  /** When true, treat 401 by clearing the token (triggers re-login). */
  clearTokenOn401?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const base = baseUrl().replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${base}${cleanPath}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function request<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<ApiResult<T>> {
  const { method = "GET", body, query, anonymous, clearTokenOn401 = true } = opts;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (!anonymous) {
    const tok = tokenStorage.get();
    if (tok) headers.Authorization = `Bearer ${tok}`;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new ApiError(
      `Network error reaching the server: ${msg}. Is the backend running at ${baseUrl()}?`,
      0,
    );
  }

  let json: ApiEnvelope<T> | null = null;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text) as ApiEnvelope<T>;
    } catch {
      throw new ApiError(`Invalid JSON from server (HTTP ${res.status})`, res.status);
    }
  }

  if (!res.ok) {
    if (res.status === 401 && clearTokenOn401) {
      tokenStorage.clear();
    }
    const msg = json?.error || json?.message || `Request failed with HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }

  if (json && json.success === false) {
    throw new ApiError(json.error || json.message || "Request failed", res.status);
  }

  return {
    data: (json?.data ?? (undefined as unknown)) as T,
    meta: json?.meta,
    message: json?.message,
  };
}

export const apiBaseUrl = baseUrl;
